import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import { parseErrorMessage } from '../utils/formErrors';
import { isEventOver } from '../utils/eventHelpers';

/**
 * Data-fetching and mutation logic for the Event Series Management admin page.
 *
 * Owns the series list, the host-eligible users list, the "manage events"
 * modal's event list/reschedule draft values, and all series/event mutation
 * handlers (create/update/delete series, generate events, toggle active,
 * assign/remove host, quick-assign featured game, delete/reschedule an
 * upcoming event). `isAdmin` gates the initial fetch, mirroring the original
 * page's effect.
 */
export function useEventSeriesManagement(isAdmin) {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [unlinkedEvents, setUnlinkedEvents] = useState([]);

  const [managingEvents, setManagingEvents] = useState(null);
  const [seriesEvents, setSeriesEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [rescheduleValues, setRescheduleValues] = useState({});
  const [updatingEvent, setUpdatingEvent] = useState(null);

  const fetchSeries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/event-series');
      setSeries(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load event series');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await apiService.get('/users?hostEligible=true');
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  // Upcoming events with no series behind them - orphans left by a deleted series, plus
  // anything created by hand. Listed for review; nothing is removed without being picked.
  const fetchUnlinkedEvents = useCallback(async () => {
    try {
      const response = await apiService.get('/event-series/unlinked-events/upcoming');
      setUnlinkedEvents(response.data || []);
    } catch (err) {
      console.error('Error fetching unlinked events:', err);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchSeries();
      fetchUsers();
      fetchUnlinkedEvents();
    }
  }, [isAdmin, fetchSeries, fetchUsers, fetchUnlinkedEvents]);

  // Tonight's date is still upcoming while the doors are open: a series whose only
  // remaining date has started should not read as having none left.
  const getUpcomingEvents = useCallback((events = []) => {
    return events.filter((event) => !isEventOver(event));
  }, []);

  const getEventRsvpCount = useCallback((event) => {
    if (typeof event.rsvp_count === 'number') {
      return event.rsvp_count;
    }
    if (typeof event.rsvp_count === 'string') {
      return parseInt(event.rsvp_count, 10) || 0;
    }
    if (Array.isArray(event.rsvps)) {
      return event.rsvps.length;
    }
    return 0;
  }, []);

  const refreshManagedEvents = useCallback(async (seriesId) => {
    const response = await apiService.get(`/events?series_id=${seriesId}`);
    const upcomingEvents = getUpcomingEvents(response.data);
    setSeriesEvents(upcomingEvents);

    const nextRescheduleValues = {};
    upcomingEvents.forEach((event) => {
      if (event.event_date) {
        nextRescheduleValues[event.id] = event.event_date.slice(0, 16);
      }
    });
    setRescheduleValues(nextRescheduleValues);
  }, [getUpcomingEvents]);

  const handleManageEvents = useCallback(async (seriesItem) => {
    setManagingEvents(seriesItem);
    setLoadingEvents(true);
    try {
      await refreshManagedEvents(seriesItem.id);
    } catch (err) {
      console.error('Error fetching series events:', err);
      setError('Failed to load events for this series');
    } finally {
      setLoadingEvents(false);
    }
  }, [refreshManagedEvents]);

  const handleCloseManageEvents = useCallback(() => {
    setManagingEvents(null);
    setSeriesEvents([]);
    setRescheduleValues({});
  }, []);

  const assignHost = useCallback(async (eventId, userId) => {
    setUpdatingEvent(eventId);
    try {
      await apiService.post(`/events/${eventId}/assign-host`, { userId });
      await refreshManagedEvents(managingEvents.id);
    } finally {
      setUpdatingEvent(null);
    }
  }, [managingEvents, refreshManagedEvents]);

  const removeHost = useCallback(async (eventId, userId) => {
    setUpdatingEvent(eventId);
    try {
      await apiService.delete(`/events/${eventId}/remove-host/${userId}`);
      await refreshManagedEvents(managingEvents.id);
    } finally {
      setUpdatingEvent(null);
    }
  }, [managingEvents, refreshManagedEvents]);

  const quickAssignGame = useCallback(async (eventId, gameName, gameDescription) => {
    const targetEvent = seriesEvents.find((event) => event.id === eventId);
    setUpdatingEvent(eventId);
    try {
      await apiService.put(`/events/${eventId}`, {
        event_metadata: {
          ...(targetEvent?.event_metadata || {}),
          game_name: gameName,
          game_mechanics_description: gameDescription
        }
      });
      await refreshManagedEvents(managingEvents.id);
    } finally {
      setUpdatingEvent(null);
    }
  }, [seriesEvents, managingEvents, refreshManagedEvents]);

  const deleteUpcomingEvent = useCallback(async (eventId) => {
    setUpdatingEvent(eventId);
    try {
      await apiService.delete(`/events/${eventId}`);
      await refreshManagedEvents(managingEvents.id);
    } finally {
      setUpdatingEvent(null);
    }
  }, [managingEvents, refreshManagedEvents]);

  const rescheduleEvent = useCallback(async (eventId, isoDateString) => {
    setUpdatingEvent(eventId);
    try {
      await apiService.put(`/events/${eventId}`, {
        event_date: isoDateString
      });
      await refreshManagedEvents(managingEvents.id);
    } finally {
      setUpdatingEvent(null);
    }
  }, [managingEvents, refreshManagedEvents]);

  const setRescheduleValue = useCallback((eventId, value) => {
    setRescheduleValues((prev) => ({ ...prev, [eventId]: value }));
  }, []);

  const submitSeries = useCallback(async (formData, editingSeriesId) => {
    try {
      const response = editingSeriesId
        ? await apiService.put(`/event-series/${editingSeriesId}`, formData)
        : await apiService.post('/event-series', formData);
      await fetchSeries();
      setError(null);
      return response?.data;
    } catch (err) {
      // The caller renders the field-level detail; this is only the list-level message
      setError(parseErrorMessage(err, editingSeriesId ? 'Failed to update series' : 'Failed to create series'));
      console.error('Error:', err);
      throw err;
    }
  }, [fetchSeries]);

  const deleteUnlinkedEvents = useCallback(async (eventIds) => {
    const response = await apiService.post('/event-series/unlinked-events/delete', { event_ids: eventIds });
    await fetchUnlinkedEvents();
    await fetchSeries();
    return response.data;
  }, [fetchUnlinkedEvents, fetchSeries]);

  const deleteSeries = useCallback(async (id) => {
    try {
      const response = await apiService.delete(`/event-series/${id}`);
      await fetchSeries();
      setError(null);
      return response.data;
    } catch (err) {
      setError('Failed to delete series');
      console.error('Error:', err);
      throw err;
    }
  }, [fetchSeries]);

  const generateEvents = useCallback(async (id) => {
    try {
      const response = await apiService.post(`/event-series/${id}/generate-events`, {
        preview_only: false
      });
      setError(null);
      return response.data.events_created;
    } catch (err) {
      setError('Failed to generate events');
      console.error('Error:', err);
      throw err;
    }
  }, []);

  const toggleSeriesActive = useCallback(async (id, currentStatus) => {
    try {
      await apiService.put(`/event-series/${id}`, {
        is_active: !currentStatus
      });
      await fetchSeries();
      setError(null);
    } catch (err) {
      setError('Failed to update series status');
      console.error('Error:', err);
      throw err;
    }
  }, [fetchSeries]);

  return {
    series,
    loading,
    error,
    users,
    fetchSeries,
    managingEvents,
    seriesEvents,
    loadingEvents,
    rescheduleValues,
    updatingEvent,
    getEventRsvpCount,
    handleManageEvents,
    handleCloseManageEvents,
    assignHost,
    removeHost,
    quickAssignGame,
    deleteUpcomingEvent,
    rescheduleEvent,
    setRescheduleValue,
    submitSeries,
    deleteSeries,
    unlinkedEvents,
    fetchUnlinkedEvents,
    deleteUnlinkedEvents,
    generateEvents,
    toggleSeriesActive
  };
}

export default useEventSeriesManagement;
